import {
  PolicyholderType,
  type HomeFixtures,
  type HomeType,
  type OrganizationRelationshipType,
  type OrganizationType,
  type Relationship,
  type RoofShape,
  type SkirtingType,
  type UnitLocation,
} from "@covertree/new-business-graphql";
import type { GetEndorsementQuery } from "@covertree/agent-portal-graphql";

import type {
  InsuredTabAdditionalInterest,
  InsuredTabFormValues,
  InsuredTabPolicy,
} from "./types";

/** The `getEndorsement` result, as selected by the GetEndorsement operation. */
export type EndorsementDraft = NonNullable<
  GetEndorsementQuery["getEndorsement"]
>;

/** Map a policy to the Insured Info form default values. */
export const buildInsuredTabFormValuesFromPolicy = (
  policy: InsuredTabPolicy
): InsuredTabFormValues => {
  const unit = policy?.units?.[0];
  const propertyAddress = unit?.address;
  const construction = unit?.construction;
  const unitDetails = unit?.details;
  const policyAdditionalInsured = policy?.additionalInsured?.[0];

  return {
    name: "",
    endorsementEffectiveDate: null,
    reason: "",
    // Policy enum (agent-portal) and form enum (new-business) share string
    // values but are nominally distinct, so cast across.
    type: (policy?.policyholder?.type ??
      PolicyholderType.Person) as unknown as PolicyholderType,
    firstName: policy?.policyholder?.firstName ?? "",
    middleName: policy?.policyholder?.middleName ?? "",
    lastName: policy?.policyholder?.lastName ?? "",
    insuredDateOfBirth: policy?.insuredDateOfBirth ?? "",
    orgName: policy?.policyholder?.orgName ?? "",
    orgType: (policy?.policyholder?.orgType ?? undefined) as
      | OrganizationType
      | undefined,
    repRelationToOrg: (policy?.policyholder?.repRelationToOrg ?? undefined) as
      | OrganizationRelationshipType
      | undefined,
    describeOrgType: policy?.policyholder?.describeOrgType ?? "",
    additionalInsured: policyAdditionalInsured
      ? {
          firstName: policyAdditionalInsured.adFirstName,
          lastName: policyAdditionalInsured.adLastName,
          relationship:
            policyAdditionalInsured.relationshipIndividual as unknown as Relationship,
        }
      : undefined,
    propertyStreetAddress: propertyAddress?.streetAddress ?? "",
    propertyUnitNumber: propertyAddress?.unitNumber ?? "",
    propertyCity: propertyAddress?.city ?? "",
    propertyState: propertyAddress?.state ?? "",
    propertyZipCode: propertyAddress?.zipCode ?? "",
    additionalInterests: policy?.additionalInterests ?? [],
    manufacturer: construction?.manufacturerName
      ? {
          label: construction.manufacturerName,
          value: construction.manufacturerName,
        }
      : undefined,
    homeType: (construction?.homeType ?? undefined) as HomeType | undefined,
    makeCustom: construction?.makeCustom ?? undefined,
    modelHome: construction?.modelHome ?? undefined,
    modelYear: construction?.modelYear
      ? {
          label: construction.modelYear.toString(),
          value: construction.modelYear,
        }
      : undefined,
    totalSquareFootage: construction?.totalSquareFootage ?? undefined,
    homeWidth: construction?.homeWidth ?? undefined,
    homeLength: construction?.homeLength ?? undefined,
    location: (unitDetails?.unitLocation ?? undefined) as
      | UnitLocation
      | undefined,
    communityName: unitDetails?.communityName
      ? {
          label: unitDetails.communityName,
          value: unitDetails.communityName,
        }
      : undefined,
    roofShape: (construction?.roofShape ?? undefined) as RoofShape | undefined,
    roofYearYyyy: construction?.roofYearYyyy ?? undefined,
    homeHudNumber: construction?.homeHudNumber ?? undefined,
    skirtingType: (construction?.skirtingType ?? undefined) as
      | SkirtingType
      | undefined,
    homeFixtures: (construction?.homeFixtures ?? undefined) as
      | HomeFixtures
      | undefined,
  };
};

/**
 * The `projectedPolicy` shape both endorsement operations satisfy.
 *
 * Declared structurally because `getEndorsement` and `createOrUpdateEndorsement`
 * select overlapping but unequal subsets — the mutation also selects
 * `unitLevelCoverages` — so neither generated type is assignable to the other.
 */
export interface InsuredTabProjectedPolicy {
  additionalInterests?: InsuredTabAdditionalInterest[] | null;
  additionalInsured?: ReadonlyArray<{
    adFirstName: string;
    adLastName: string;
    relationshipIndividual?: unknown;
  }> | null;
  units?: ReadonlyArray<{
    address?: { unitNumber?: string | null } | null;
    construction?: {
      homeFixtures?: unknown;
      homeHudNumber?: string | null;
      homeLength?: number | null;
      homeType?: unknown;
      homeWidth?: number | null;
      makeCustom?: string | null;
      manufacturerName?: string | null;
      modelHome?: string | null;
      modelYear?: number | null;
      roofShape?: unknown;
      roofYearYyyy?: number | null;
      skirtingType?: unknown;
      totalSquareFootage?: number | null;
    } | null;
    details?: {
      communityName?: string | null;
      unitLocation?: unknown;
    } | null;
  }> | null;
}

/**
 * Map a projected policy to the Insured Info fields **an endorsement can change**.
 *
 * This is the ownership split, written down once. The projection owns every field
 * an endorsement can actually modify. It deliberately emits nothing else, so a
 * caller can overlay it on live form state without disturbing:
 *
 *  - the effective date and agent memo — draft-level, and only `getEndorsement`
 *    returns them (the mutation's `reason` is a *block* reason). Re-emitting them
 *    as `null`/`""` here is precisely what blanked the effective date after a
 *    reprice.
 *  - the policyholder block and street/city/state/zip — an endorsement cannot
 *    change them, they are read-only in the form, and the projection does not
 *    carry them.
 *
 * Every key it does emit is emitted unconditionally when the source object is
 * present, so switching a value back to empty still propagates.
 */
export const buildInsuredTabProjection = (
  projectedPolicy: InsuredTabProjectedPolicy | null | undefined
): Partial<InsuredTabFormValues> => {
  const unit = projectedPolicy?.units?.[0];
  const construction = unit?.construction;
  const details = unit?.details;
  const additionalInsured = projectedPolicy?.additionalInsured?.[0];

  const overlay: Partial<InsuredTabFormValues> = {};

  /**
   * Assigns only when the projection actually carries a value.
   *
   * Critical: this overlay is spread **over** the policy baseline, and a spread
   * key holding `undefined` still wins. Emitting absent fields as `undefined`
   * therefore blanked the policy's real value.
   * An absent field means "the projection has nothing to say", not "empty".
   */
  const set = <K extends keyof InsuredTabFormValues>(
    key: K,
    value: InsuredTabFormValues[K] | null | undefined
  ) => {
    if (value != null) overlay[key] = value;
  };

  if (additionalInsured) {
    overlay.additionalInsured = {
      firstName: additionalInsured.adFirstName,
      lastName: additionalInsured.adLastName,
      relationship:
        additionalInsured.relationshipIndividual as unknown as Relationship,
    };
  }

  // The unit's address number is the resolved `lotUnit`: `buildEndorsementInput`
  // sends `propertyUnitNumber` as `lotUnit`, so map it back so an edited unit
  // number re-hydrates instead of falling back to the policy's value.
  set("propertyUnitNumber", unit?.address?.unitNumber);

  set("location", details?.unitLocation as UnitLocation | undefined);
  set(
    "communityName",
    details?.communityName
      ? { label: details.communityName, value: details.communityName }
      : undefined
  );

  set(
    "manufacturer",
    construction?.manufacturerName
      ? {
          label: construction.manufacturerName,
          value: construction.manufacturerName,
        }
      : undefined
  );
  set("homeType", construction?.homeType as HomeType | undefined);
  set("makeCustom", construction?.makeCustom);
  set("modelHome", construction?.modelHome);
  set(
    "modelYear",
    construction?.modelYear
      ? {
          label: construction.modelYear.toString(),
          value: construction.modelYear,
        }
      : undefined
  );
  set("totalSquareFootage", construction?.totalSquareFootage);
  set("homeWidth", construction?.homeWidth);
  set("homeLength", construction?.homeLength);
  set("roofShape", construction?.roofShape as RoofShape | undefined);
  set("roofYearYyyy", construction?.roofYearYyyy);
  set("homeHudNumber", construction?.homeHudNumber);
  set("skirtingType", construction?.skirtingType as SkirtingType | undefined);
  set("homeFixtures", construction?.homeFixtures as HomeFixtures | undefined);

  // Carries the ids the backend assigned, which the next save keys on.
  set("additionalInterests", projectedPolicy?.additionalInterests);

  return overlay;
};

/**
 * Map a saved endorsement draft to the Insured Info overlay used for the form's
 * **initial** hydration when a draft is opened.
 *
 * The projection plus the two draft-level fields only `getEndorsement` returns.
 * Used once, by `loadEndorsement`; post-save refreshes use
 * {@link buildInsuredTabProjection} directly so they cannot touch these two.
 */
export const buildInsuredTabOverlayFromDraft = (
  draft: EndorsementDraft
): Partial<InsuredTabFormValues> => ({
  endorsementEffectiveDate: draft.effectiveDate ?? null,
  reason: draft.reason ?? "",
  ...buildInsuredTabProjection(draft.projectedPolicy),
});
